import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { AlertFieldCondition, AlertRuleDto, CheckSummaryDto } from '@ops-monitor/shared';

import { alerts, channels } from '../api/client';
import { debounceEta } from '../lib/debounce';
import { buildRows } from './CheckDrawer';
import { Button } from './ui';

/**
 * Per-check alert settings (one rule per check). Three independent triggers,
 * OR-ed together:
 *  - по статусу — fire when the check reaches «внимание»/«проблема»;
 *  - по значению — fire when the plotted scalar crosses a bound;
 *  - по строкам таблицы — fire when a tabular row matches
 *    (status = NOT_SYNCED и count > 100 — per-status thresholds).
 * Plus rule-level knobs: the fire debounce («событий подряд» — Slack pages
 * only after N CONSECUTIVE tripped evaluations; a blip that clears before N
 * stays silent, network wobble on flaky probes — the row hint also converts
 * N into wall-clock hours via the check's own interval, the 2026-10-30
 * smsaero lesson), the actuality window («активно с … до … МСК»), the Slack
 * mentions («Слэк-ID через запятую» — only the fire message pokes people,
 * the recovery does not) and the delivery channels (empty = «По умолчанию»).
 * The folded «Как работает это оповещение» block at the top explains all of
 * it from the check's own why/description + the live form.
 */

/** fires → «1 проверку / 3 проверки / 5 проверок» for the logic block. */
function firesWord(fires: number): string {
  const mod100 = ((fires % 100) + 100) % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'проверок';
  const mod10 = ((fires % 10) + 10) % 10;
  if (mod10 === 1) return 'проверку';
  if (mod10 >= 2 && mod10 <= 4) return 'проверки';
  return 'проверок';
}

export function AlertSettings({ check }: { check: CheckSummaryDto }) {
  const queryClient = useQueryClient();
  const rule = useQuery({
    queryKey: ['alert-rule', check.id],
    queryFn: ({ signal }) => alerts.get(check.id, signal),
  });
  const channelList = useQuery({ queryKey: ['channels'], queryFn: ({ signal }) => channels.list(signal) });

  const [enabled, setEnabled] = useState(false);
  const [level, setLevel] = useState<'warn' | 'crit'>('crit');
  const [useThreshold, setUseThreshold] = useState(false);
  const [threshold, setThreshold] = useState('');
  const [direction, setDirection] = useState<'above' | 'below'>('above');
  const [conditions, setConditions] = useState<AlertFieldCondition[]>([]);
  const [fires, setFires] = useState(1);
  const [timeFrom, setTimeFrom] = useState('00:00');
  const [timeTo, setTimeTo] = useState('23:59');
  const [retention, setRetention] = useState(5);
  const [mentions, setMentions] = useState('');
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  // (Re)hydrate the form whenever the drawer opens for another check.
  useEffect(() => {
    if (loadedId === check.id || rule.isLoading) return;
    const data = rule.data;
    if (data && 'checkId' in data) {
      setEnabled(data.enabled);
      setLevel(data.level);
      setUseThreshold(data.threshold !== null);
      setThreshold(data.threshold === null ? '' : String(data.threshold));
      setDirection(data.direction);
      setConditions(data.fieldConditions);
      setFires(data.consecutiveFires ?? 1);
      setTimeFrom(data.timeFrom ?? '00:00');
      setTimeTo(data.timeTo ?? '23:59');
      setRetention(data.eventRetentionDays ?? 5);
      setMentions(data.mentionSlackIds ?? '');
      setChannelIds(data.channelIds ?? []);
      setLoadedId(check.id);
    } else if (data && 'exists' in data && data.exists === false) {
      setEnabled(false);
      setLevel('crit');
      setUseThreshold(false);
      setThreshold('');
      setDirection('above');
      setConditions([]);
      setFires(1);
      setTimeFrom('00:00');
      setTimeTo('23:59');
      setRetention(5);
      setMentions('');
      setChannelIds([]);
      setLoadedId(check.id);
    }
  }, [rule.data, rule.isLoading, check.id, loadedId]);

  const save = useMutation({
    mutationFn: () =>
      alerts.upsert(check.id, {
        enabled,
        level,
        threshold: useThreshold && threshold !== '' ? Number(threshold) : null,
        direction,
        fieldConditions: conditions.filter((c) => c.field && c.equals && c.metric && !Number.isNaN(c.threshold)),
        consecutiveFires: Math.max(1, Math.min(60, Math.floor(fires) || 1)),
        timeFrom: timeFrom || '00:00',
        timeTo: timeTo || '23:59',
        eventRetentionDays: Math.max(1, Math.min(365, Math.floor(retention) || 5)),
        mentionSlackIds: mentions.trim(),
        channelIds,
      }),
    onSuccess: (data) => {
      setError(null);
      const parts: string[] = [];
      if (data.enabled) {
        if (data.fieldConditions.length > 0) parts.push(`условий по строкам: ${data.fieldConditions.length}`);
        if (data.threshold !== null) parts.push(`порог ${data.direction === 'below' ? '<' : '>'} ${data.threshold}`);
        parts.push(data.level === 'crit' ? 'при «проблема»' : 'при «внимание» или хуже');
        if (data.consecutiveFires > 1) parts.push(`после ${data.consecutiveFires} событий подряд (${debounceEta(data.consecutiveFires, check.intervalSec)})`);
        if (data.eventRetentionDays !== 5) parts.push(`хранить события ${data.eventRetentionDays} дн.`);
        if (data.channelIds.length > 0) parts.push(`каналов: ${data.channelIds.length}`);
        setSaved(`Сохранено — оповещения включены (${parts.join('; ')})`);
      } else {
        setSaved('Сохранено — оповещения выключены');
      }
      void queryClient.invalidateQueries({ queryKey: ['alert-rule', check.id] });
      void queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
    onError: () => setError('Не удалось сохранить'),
  });

  const dto: AlertRuleDto | null = rule.data && 'checkId' in rule.data ? rule.data : null;

  // Observed rows/columns power the condition builder's selects.
  const rows = buildRows(check.value);
  const columns = rows.length > 0 ? Object.keys(rows[0]!) : [];
  const numericColumns = columns.filter((key) => rows.some((row) => typeof row[key] === 'number'));
  const distinctValues = (field: string): string[] =>
    Array.from(new Set(rows.map((row) => String(row[field] ?? '')).filter((v) => v !== ''))).slice(0, 50);

  const addCondition = (): void => {
    const field = columns[0] ?? '';
    const metric = numericColumns.find((c) => c !== field) ?? numericColumns[0] ?? '';
    setConditions([
      ...conditions,
      { field, equals: distinctValues(field)[0] ?? '', metric, threshold: 100, direction: 'above' },
    ]);
  };

  const patchCondition = (index: number, patch: Partial<AlertFieldCondition>): void => {
    setConditions(conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  return (
    <div className="alert-box">
      <details className="alert-box__details">
        <summary>Как работает это оповещение</summary>
        <div className="alert-box__details-body">
          <p>
            <strong>Что смотрит проверка.</strong> {check.why}
          </p>
          {check.description ? <p>{check.description}</p> : null}
          <p>
            <strong>Как срабатывает.</strong> Триггеры складываются через ИЛИ —{' '}
            {useThreshold
              ? 'числовой порог из полей ниже'
              : level === 'crit'
                ? 'плитка в статусе «проблема»'
                : 'плитка в статусе «внимание» или хуже'}
            {conditions.length > 0 ? `, плюс ${conditions.length} усл. по строкам таблицы` : ''}. Slack уходит, когда условие
            выполняется {fires} {firesWord(fires)} подряд (гистерезис: одиночные блипы молчат, счётчик обнуляет первый
            «нормальный» запуск; {fires} {firesWord(fires)} × интервал проверки = первое оповещение{' '}
            {debounceEta(Math.max(1, Math.floor(fires) || 1), check.intervalSec)}); при возврате в норму приходит одно
            «восстановилось» (без упоминаний). Оповещения активны только с {timeFrom} до {timeTo} МСК (окно может
            переходить через полночь) — вне окна полная тишина, счётчик подряд замирает.
            {mentions.trim() ? ` В сообщении о проблеме упоминаются: ${mentions.trim()}.` : ''}
            {channelIds.length > 0
              ? ` Доставляется в выбранные каналы (${channelIds.length}).`
              : ' Доставляется в канал «По умолчанию».'}{' '}
            Текущие порог и условия видны в полях ниже.
          </p>
        </div>
      </details>
      <div className="alert-box__row">
        <label style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          Оповещать в Slack
        </label>
        {dto?.lastState === 'alerting' ? (
          <span className="alert-box__state alert-box__state--alerting">сейчас в состоянии оповещения</span>
        ) : null}
        {dto?.lastNotifiedAt ? (
          <span className="alert-box__state">
            последнее оповещение: {new Date(dto.lastNotifiedAt).toLocaleString('ru-RU')}
          </span>
        ) : null}
      </div>

      <div className="alert-box__row">
        <label className="alert-box__radio">
          <input type="radio" name={`alert-mode-${check.id}`} checked={!useThreshold} onChange={() => setUseThreshold(false)} />
          по статусу
        </label>
        <select value={level} disabled={useThreshold} onChange={(event) => setLevel(event.target.value as 'warn' | 'crit')} aria-label="Уровень статуса">
          <option value="crit">при «проблема»</option>
          <option value="warn">при «внимание» или хуже</option>
        </select>
        <label className="alert-box__radio">
          <input type="radio" name={`alert-mode-${check.id}`} checked={useThreshold} onChange={() => setUseThreshold(true)} />
          по значению
        </label>
        <select value={direction} disabled={!useThreshold} onChange={(event) => setDirection(event.target.value as 'above' | 'below')} aria-label="Направление порога">
          <option value="above">выше</option>
          <option value="below">ниже</option>
        </select>
        <input
          type="number"
          placeholder="порог"
          disabled={!useThreshold}
          value={threshold}
          onChange={(event) => setThreshold(event.target.value)}
          aria-label="Порог значения"
        />
      </div>

      {columns.length > 0 ? (
        <div className="alert-box__conditions">
          {conditions.map((condition, index) => (
            <div key={index} className="alert-box__row alert-box__condition">
              <span className="alert-box__when">когда</span>
              <select value={condition.field} onChange={(event) => patchCondition(index, { field: event.target.value, equals: distinctValues(event.target.value)[0] ?? '' })} aria-label="Колонка">
                {columns.map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
              <span>=</span>
              <select value={condition.equals} onChange={(event) => patchCondition(index, { equals: event.target.value })} aria-label="Значение">
                {distinctValues(condition.field).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <select value={condition.metric} onChange={(event) => patchCondition(index, { metric: event.target.value })} aria-label="Метрика">
                {(numericColumns.length > 0 ? numericColumns : columns).map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
              <select value={condition.direction} onChange={(event) => patchCondition(index, { direction: event.target.value as 'above' | 'below' })} aria-label="Направление">
                <option value="above">выше</option>
                <option value="below">ниже</option>
              </select>
              <input
                type="number"
                value={Number.isNaN(condition.threshold) ? '' : condition.threshold}
                onChange={(event) => patchCondition(index, { threshold: Number(event.target.value) })}
                aria-label="Порог"
              />
              <button type="button" className="alert-box__remove" onClick={() => setConditions(conditions.filter((_, i) => i !== index))} aria-label="Удалить условие">
                ×
              </button>
            </div>
          ))}
          <button type="button" className="btn btn--ghost btn--sm" onClick={addCondition}>
            + условие по строке таблицы
          </button>
        </div>
      ) : null}

      <div className="alert-box__row">
        <span className="alert-box__when">срабатывать после</span>
        <input
          type="number"
          min={1}
          max={60}
          value={fires}
          onChange={(event) => setFires(Number(event.target.value))}
          aria-label="Событий подряд до срабатывания"
          style={{ width: 72 }}
        />
        <span className="alert-box__state">
          событий подряд (1 — сразу; сетевые блипы не алармят) — при интервале проверки{' '}
          {check.intervalSec >= 3600
            ? `${Math.round(check.intervalSec / 3600)} ч`
            : `${Math.round(check.intervalSec / 60)} мин`}{' '}
          первое оповещение {debounceEta(Math.max(1, Math.floor(fires) || 1), check.intervalSec)}
        </span>
      </div>

      <div className="alert-box__row">
        <span className="alert-box__when">активно с</span>
        <input type="time" value={timeFrom} onChange={(event) => setTimeFrom(event.target.value)} aria-label="Время начала" />
        <span className="alert-box__when">до</span>
        <input type="time" value={timeTo} onChange={(event) => setTimeTo(event.target.value)} aria-label="Время окончания" />
        <span className="alert-box__state">МСК — вне окна оповещения молчат (окно через полночь поддерживается)</span>
      </div>

      <div className="alert-box__row">
        <span className="alert-box__when">упоминания</span>
        <input
          type="text"
          placeholder="U0ABC123,U0DEF456"
          value={mentions}
          onChange={(event) => setMentions(event.target.value)}
          aria-label="Слэк-ID через запятую (упоминания)"
          style={{ minWidth: 220 }}
        />
        <span className="alert-box__state">Слэк-ID через запятую (упоминания) — только в сообщении о проблеме</span>
      </div>

      {channelList.data && channelList.data.items.length > 0 ? (
        <div className="alert-box__row">
          <span className="alert-box__when">каналы</span>
          {channelList.data.items.map((channel) => (
            <label key={channel.id} className="alert-box__radio" title={channel.isDefault ? 'канал по умолчанию' : undefined}>
              <input
                type="checkbox"
                checked={channelIds.includes(channel.id)}
                onChange={(event) =>
                  setChannelIds(
                    event.target.checked ? [...channelIds, channel.id] : channelIds.filter((id) => id !== channel.id),
                  )
                }
              />
              {channel.name}
              {channel.isDefault ? ' ★' : ''}
            </label>
          ))}
          <span className="alert-box__state">{channelIds.length === 0 ? 'ничего не выбрано — «По умолчанию»' : `выбрано: ${channelIds.length}`}</span>
        </div>
      ) : null}

      <div className="alert-box__row">
        <span className="alert-box__when">хранить события</span>
        <input
          type="number"
          min={1}
          max={365}
          value={retention}
          onChange={(event) => setRetention(Number(event.target.value))}
          aria-label="Дней хранения событий"
          style={{ width: 72 }}
        />
        <span className="alert-box__state">дней (для копящихся таблиц — напр. «Запросы длиннее 30 секунд»; старее удаляется)</span>
      </div>

      <div className="alert-box__row">
        <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
        {saved ? <span className="alert-box__state">{saved}</span> : null}
        {error ? <span className="form-error">{error}</span> : null}
      </div>
      <span className="alert-box__state">
        Условия складываются (ИЛИ): сработает первое выполнившееся. Без условий по строкам — оповещение приходит, когда плитка
        окрашивается в выбранный статус. «Событий подряд» — гистерезис: условие должно выполняться N проверок кряду; если до N
        оно отпустило, счётчик обнуляется и оповещения не будет вовсе.
      </span>
    </div>
  );
}
